/* One-page printable quick-start guides (v9.0) — per role, in EN / ES / FR.
   Used by UserManual.jsx "Print quick-start". Kept as plain data so kiosk
   crews can get a paper guide in the language they punch in with. */

export const QS_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

const L = {
  en: { steps: 'Your day, step by step', tips: 'Good to know', footer: 'Full manual: open HinesTime → sidebar → ? Help, or visit /user-manual' },
  es: { steps: 'Tu día, paso a paso', tips: 'Bueno saber', footer: 'Manual completo: abre HinesTime → barra lateral → ? Ayuda, o visita /user-manual' },
  fr: { steps: 'Votre journée, étape par étape', tips: 'Bon à savoir', footer: 'Manuel complet : ouvrez HinesTime → barre latérale → ? Aide, ou visitez /user-manual' },
};

export const QUICK_STARTS = {
  user: {
    en: {
      title: 'HinesTime Quick-Start — Workers',
      steps: [
        'Sign in with the email + temporary password from your admin; you\u2019ll set your own password and (optionally) a 4-digit kiosk PIN.',
        'Clock in from My Day (big green button). GPS tags your location. Your company may require the kiosk tablet or a Site QR scan instead — the screen will tell you.',
        'Open your job and check every box on the safety attestation, then tap "I attest & start work".',
        'Work the job: add photos and notes from the job card. Use "Travel" when driving between sites and "Taking a break" for breaks.',
        'Clocking out after 5+ hours with no logged break? HinesTime asks if you took a meal break — answer honestly; choosing Yes deducts an unpaid 30 minutes.',
        'Clock out at the end of your shift. Forgot? Tell your supervisor — they can fix it in payroll review.',
        'Check My Time for your hours; use "Report missing time" if a punch is wrong or missing.',
        'Need vacation or sick days? Request them from the Time Off page — your supervisor approves and your balance updates.',
      ],
      tips: [
        'No signal? Punch anyway — it saves on your device and syncs automatically with the original time.',
        'Kiosk PIN: profile menu (top right) → My Kiosk PIN.',
        'Five wrong kiosk PINs locks you out for a few minutes.',
        'Notifications (bell icon) are tappable — they take you straight to the job or page that needs you.',
      ],
    },
    es: {
      title: 'Guía rápida HinesTime — Trabajadores',
      steps: [
        'Inicia sesión con el correo y la contraseña temporal de tu administrador; luego crea tu propia contraseña y (opcional) un PIN de kiosco de 4 dígitos.',
        'Marca entrada desde Mi Día (botón verde grande). El GPS registra tu ubicación. Tu empresa puede exigir la tableta de kiosco o escanear el QR del sitio — la pantalla te lo indicará.',
        'Abre tu trabajo y marca cada casilla de la declaración de seguridad, luego toca "Certifico y comienzo a trabajar".',
        'Trabaja: agrega fotos y notas desde la tarjeta del trabajo. Usa "Viaje" al conducir entre sitios y "Tomando un descanso" para pausas.',
        '¿Sales después de 5+ horas sin descanso registrado? HinesTime pregunta si tomaste tu descanso de comida — responde con honestidad; elegir Sí descuenta 30 minutos no pagados.',
        'Marca salida al final del turno. ¿Lo olvidaste? Avisa a tu supervisor — puede corregirlo en la revisión de nómina.',
        'Revisa Mis Horas para ver tus horas; usa "Reportar tiempo faltante" si falta o está mal un registro.',
        '¿Vacaciones o días por enfermedad? Solicítalos en la página Tiempo Libre — tu supervisor aprueba y tu saldo se actualiza.',
      ],
      tips: [
        '¿Sin señal? Marca igual — se guarda en tu dispositivo y se sincroniza automáticamente con la hora original.',
        'PIN de kiosco: menú de perfil (arriba a la derecha) → Mi PIN de Kiosco.',
        'Cinco PIN incorrectos bloquean el kiosco unos minutos.',
        'Las notificaciones (campana) se pueden tocar — te llevan directo al trabajo o página que te necesita.',
      ],
    },
    fr: {
      title: 'Guide rapide HinesTime — Travailleurs',
      steps: [
        'Connectez-vous avec le courriel et le mot de passe temporaire de votre admin ; créez ensuite votre mot de passe et (option) un NIP de kiosque à 4 chiffres.',
        'Pointez depuis Ma Journée (gros bouton vert). Le GPS enregistre votre position. Votre entreprise peut exiger la tablette kiosque ou un scan du QR du site — l\u2019écran vous l\u2019indiquera.',
        'Ouvrez votre travail et cochez chaque case de l\u2019attestation de sécurité, puis touchez « J\u2019atteste et je commence ».',
        'Travaillez : ajoutez photos et notes depuis la fiche du travail. Utilisez « Déplacement » entre les sites et « Pause » pour les pauses.',
        'Vous dépointez après 5 h+ sans pause enregistrée ? HinesTime demande si vous avez pris votre pause repas — répondez honnêtement ; choisir Oui déduit 30 minutes non payées.',
        'Dépointez en fin de quart. Oublié ? Prévenez votre superviseur — il peut corriger dans la revue de paie.',
        'Consultez Mes Heures ; utilisez « Signaler du temps manquant » si un pointage est faux ou absent.',
        'Vacances ou congé maladie ? Faites la demande sur la page Congés — votre superviseur approuve et votre solde se met à jour.',
      ],
      tips: [
        'Pas de réseau ? Pointez quand même — c\u2019est enregistré sur l\u2019appareil et synchronisé automatiquement avec l\u2019heure réelle.',
        'NIP de kiosque : menu profil (en haut à droite) → Mon NIP de kiosque.',
        'Cinq NIP erronés bloquent le kiosque quelques minutes.',
        'Les notifications (cloche) sont cliquables — elles mènent directement au travail ou à la page concernée.',
      ],
    },
  },
  squad: {
    en: {
      title: 'HinesTime Quick-Start — Squad Leaders',
      steps: [
        'You\u2019re a worker with a crown: your supervisor made you leader of a squad. Everything in the Workers guide still applies to you.',
        'Open My Day — your amber squad card shows the squad name and a live "on the clock" count.',
        'Tap Clock In Squad at shift start — everyone in the squad is punched in at once (anyone already on the clock is skipped).',
        'Every member gets a notification, so nobody is punched in without knowing.',
        'Someone absent or a mutual covering? Tell your supervisor — their team clock-in roster can skip absent members (with a reason tag) and add temps for one shift.',
        'Tap Clock Out Squad at the end — hours are saved individually for payroll.',
      ],
      tips: [
        'You are not a supervisor: no reports, schedules, approvals or kiosk setup — ask your supervisor for roster changes.',
        'Your own punch is included when you clock the squad in.',
        'Auto clock-out rules still protect forgotten punches, per worker.',
      ],
    },
    es: {
      title: 'Guía rápida HinesTime — Líderes de Cuadrilla',
      steps: [
        'Eres un trabajador con corona: tu supervisor te nombró líder de una cuadrilla. Todo lo de la guía de Trabajadores también aplica para ti.',
        'Abre Mi Día — tu tarjeta ámbar de cuadrilla muestra el nombre y cuántos están trabajando.',
        'Toca Marcar Entrada de Cuadrilla al inicio del turno — todos entran a la vez (se omite a quien ya está trabajando).',
        'Cada miembro recibe una notificación, nadie es registrado sin saberlo.',
        '¿Alguien ausente o un reemplazo temporal? Avisa a tu supervisor — su lista de equipo puede omitir ausentes (con motivo) y agregar temporales por un turno.',
        'Toca Marcar Salida de Cuadrilla al final — las horas se guardan individualmente para la nómina.',
      ],
      tips: [
        'No eres supervisor: sin reportes, horarios, aprobaciones ni kiosco — pide cambios de lista a tu supervisor.',
        'Tu propia entrada se incluye al registrar la cuadrilla.',
        'Las reglas de salida automática siguen protegiendo los olvidos, por trabajador.',
      ],
    },
    fr: {
      title: 'Guide rapide HinesTime — Chefs d\u2019équipe (squad)',
      steps: [
        'Vous êtes un travailleur avec une couronne : votre superviseur vous a nommé chef d\u2019une squad. Tout le guide Travailleurs s\u2019applique aussi à vous.',
        'Ouvrez Ma Journée — votre carte ambre de squad affiche le nom et le nombre de personnes au travail.',
        'Touchez Pointer la squad au début du quart — tout le monde pointe d\u2019un coup (les personnes déjà au travail sont ignorées).',
        'Chaque membre reçoit une notification, personne n\u2019est pointé à son insu.',
        'Un absent ou un remplaçant ? Prévenez votre superviseur — sa liste d\u2019équipe peut exclure les absents (avec motif) et ajouter des temporaires pour un quart.',
        'Touchez Dépointer la squad à la fin — les heures sont enregistrées individuellement pour la paie.',
      ],
      tips: [
        'Vous n\u2019êtes pas superviseur : pas de rapports, horaires, approbations ni kiosque — demandez les changements de liste à votre superviseur.',
        'Votre propre pointage est inclus quand vous pointez la squad.',
        'Les règles de dépointage automatique protègent toujours les oublis, par travailleur.',
      ],
    },
  },
  supervisor: {
    en: {
      title: 'HinesTime Quick-Start — Supervisors',
      steps: [
        'Your day starts on the Dashboard: live tiles for who\u2019s on the clock, active jobs, and your team quick-clock bar.',
        'Clock In Team opens the roster dialog: present members are pre-checked (from today\u2019s schedule if one exists). Uncheck absentees and tag the reason — Vacation / Sick / No-show.',
        'Covering with a mutual? Use "Add someone for this shift" to punch a temp in with your team — the roster is unchanged and payroll flags their hours.',
        'Clock Out Team is selective too — uncheck anyone staying late.',
        'Clock YOURSELF in separately (top-bar pill or My Day) — clocking the team does not punch you in.',
        'Review Pending Approvals daily: time amendments, missed punches and time-off requests from your crew.',
        'Build schedules on the weekly grid; assign jobs; create squads and crown a leader so crews can punch themselves in.',
        'Set up a kiosk or Site QR posters for fixed sites from Team Management.',
      ],
      tips: [
        'Absence tags feed the No-Shows & Absences report admins read — tag consistently.',
        'Your team\u2019s reports live under Reports; you see your own teams only.',
        'Mute notifications per team if you co-supervise a busy one.',
      ],
    },
    es: {
      title: 'Guía rápida HinesTime — Supervisores',
      steps: [
        'Tu día empieza en el Panel: quién está trabajando, trabajos activos y tu barra de registro rápido de equipo.',
        'Marcar Entrada de Equipo abre la lista con casillas: los presentes vienen marcados (según el horario de hoy si existe). Desmarca ausentes y etiqueta el motivo — Vacaciones / Enfermo / No se presentó.',
        '¿Cubre un reemplazo? Usa "Agregar a alguien para este turno" para registrar un temporal con tu equipo — la lista no cambia y la nómina marca sus horas.',
        'Marcar Salida de Equipo también es selectivo — desmarca a quien se queda más tiempo.',
        'Márcate TÚ aparte (píldora superior o Mi Día) — registrar al equipo no te registra a ti.',
        'Revisa Aprobaciones Pendientes a diario: correcciones de tiempo, registros olvidados y solicitudes de tiempo libre.',
        'Crea horarios en la cuadrícula semanal; asigna trabajos; crea cuadrillas y corona un líder para que se registren solos.',
        'Configura un kiosco o carteles QR del sitio desde Gestión de Equipos.',
      ],
      tips: [
        'Las etiquetas de ausencia alimentan el reporte de Ausencias que leen los administradores — etiqueta siempre.',
        'Tus reportes están en Reportes; solo ves tus propios equipos.',
        'Silencia notificaciones por equipo si co-supervisas uno muy activo.',
      ],
    },
    fr: {
      title: 'Guide rapide HinesTime — Superviseurs',
      steps: [
        'Votre journée commence sur le Tableau de bord : qui travaille, travaux actifs et votre barre de pointage d\u2019équipe.',
        'Pointer l\u2019équipe ouvre la liste à cases : les présents sont pré-cochés (selon l\u2019horaire du jour s\u2019il existe). Décochez les absents et indiquez le motif — Vacances / Malade / Absence.',
        'Un remplaçant ? « Ajouter quelqu\u2019un pour ce quart » pointe un temporaire avec votre équipe — la liste ne change pas et la paie signale ses heures.',
        'Dépointer l\u2019équipe est aussi sélectif — décochez ceux qui restent.',
        'Pointez-VOUS séparément (pilule en haut ou Ma Journée) — pointer l\u2019équipe ne vous pointe pas.',
        'Vérifiez les Approbations en attente chaque jour : corrections, pointages manqués et demandes de congé.',
        'Créez les horaires sur la grille hebdomadaire ; assignez les travaux ; créez des squads et couronnez un chef.',
        'Configurez un kiosque ou des affiches QR de site depuis Gestion des équipes.',
      ],
      tips: [
        'Les motifs d\u2019absence alimentent le rapport Absences lu par les admins — étiquetez systématiquement.',
        'Vos rapports sont sous Rapports ; vous ne voyez que vos équipes.',
        'Coupez les notifications par équipe si vous co-supervisez une équipe très active.',
      ],
    },
  },
  admin: {
    en: {
      title: 'HinesTime Quick-Start — Administrators',
      steps: [
        'Set up the company: Settings → company profile, customers, projects, overtime & auto clock-out rules, time-off policies.',
        'Add staff (with hourly rates for pay estimates) and build teams; assign one or more supervisors per team.',
        'Choose each team\u2019s clock-in method: any / kiosk only / QR required (workers only — supervisors are never blocked).',
        'During the period, watch the Dashboard and actionable notifications; drill into any tile.',
        'Before payroll: Reports → Payroll (Workbench). Fix sessions, check flags (Kiosk, Offline, Temp, Edited, meal-break deductions), review pay columns (Rate / Reg / OT / Gross), run the pre-lock checklist, then lock the period.',
        'Reconcile billing with Hours by Project and Hours by Job — variance alerts flag payroll-vs-job-hour mismatches; filter by customer.',
        'Check No-Shows & Absences to see who was scheduled but never punched in, with supervisor-tagged reasons.',
        'Push approved time to QuickBooks or export CSVs; schedule any report to land in your inbox automatically.',
      ],
      tips: [
        'You inherit every supervisor and worker capability.',
        'Job labels (e.g. EMERGENCY) can carry billing multipliers — see Hours by Label.',
        'The audit log records every sensitive change with who/when/why.',
      ],
    },
    es: {
      title: 'Guía rápida HinesTime — Administradores',
      steps: [
        'Configura la empresa: Ajustes → perfil, clientes, proyectos, reglas de horas extra y salida automática, políticas de tiempo libre.',
        'Agrega personal (con tarifas por hora para estimar pagos) y crea equipos; asigna uno o más supervisores por equipo.',
        'Elige el método de entrada de cada equipo: cualquiera / solo kiosco / QR obligatorio (solo trabajadores — supervisores nunca se bloquean).',
        'Durante el período, vigila el Panel y las notificaciones accionables; entra en cualquier mosaico.',
        'Antes de la nómina: Reportes → Nómina (Workbench). Corrige sesiones, revisa marcas (Kiosco, Sin conexión, Temporal, Editado, descuentos de comida), columnas de pago (Tarifa / Regular / Extra / Bruto), pasa la lista previa y bloquea el período.',
        'Concilia la facturación con Horas por Proyecto y Horas por Trabajo — las alertas de variación marcan diferencias nómina vs. horas de trabajo; filtra por cliente.',
        'Revisa Ausencias y No Presentados: quién estaba programado y nunca marcó, con motivos etiquetados por supervisores.',
        'Envía el tiempo aprobado a QuickBooks o exporta CSV; programa cualquier reporte para recibirlo por correo.',
      ],
      tips: [
        'Heredas todas las capacidades de supervisor y trabajador.',
        'Las etiquetas de trabajo (p. ej. EMERGENCIA) pueden llevar multiplicadores de facturación — mira Horas por Etiqueta.',
        'El registro de auditoría guarda cada cambio sensible con quién/cuándo/por qué.',
      ],
    },
    fr: {
      title: 'Guide rapide HinesTime — Administrateurs',
      steps: [
        'Configurez l\u2019entreprise : Paramètres → profil, clients, projets, règles d\u2019heures sup. et de dépointage auto, politiques de congés.',
        'Ajoutez le personnel (avec taux horaires pour estimer la paie) et créez les équipes ; assignez un ou plusieurs superviseurs.',
        'Choisissez le mode de pointage de chaque équipe : libre / kiosque seulement / QR obligatoire (travailleurs seulement — jamais les superviseurs).',
        'Pendant la période, surveillez le Tableau de bord et les notifications actionnables.',
        'Avant la paie : Rapports → Paie (Workbench). Corrigez les sessions, vérifiez les indicateurs (Kiosque, Hors-ligne, Temporaire, Modifié, déductions repas), les colonnes de paie (Taux / Régulier / Sup. / Brut), passez la liste de pré-verrouillage puis verrouillez.',
        'Rapprochez la facturation avec Heures par Projet et Heures par Travail — les alertes d\u2019écart signalent les différences paie vs heures travaux ; filtrez par client.',
        'Consultez Absences : qui était planifié sans jamais pointer, avec les motifs des superviseurs.',
        'Poussez le temps approuvé vers QuickBooks ou exportez en CSV ; programmez l\u2019envoi automatique de tout rapport.',
      ],
      tips: [
        'Vous héritez de toutes les capacités superviseur et travailleur.',
        'Les étiquettes de travail (ex. URGENCE) peuvent porter des multiplicateurs de facturation — voir Heures par Étiquette.',
        'Le journal d\u2019audit enregistre chaque changement sensible avec qui/quand/pourquoi.',
      ],
    },
  },
};

export function buildQuickStartHtml(role, lang) {
  const guide = (QUICK_STARTS[role] || QUICK_STARTS.user)[lang] || (QUICK_STARTS[role] || QUICK_STARTS.user).en;
  const labels = L[lang] || L.en;
  const steps = guide.steps.map((s, i) => `<li><span class="n">${i + 1}</span><span>${s}</span></li>`).join('');
  const tips = guide.tips.map((t) => `<li>${t}</li>`).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${guide.title}</title>
<style>
  @page { margin: 14mm; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .head { display: flex; align-items: center; gap: 10px; border-bottom: 3px solid #1E88E5; padding-bottom: 10px; }
  .logo { width: 34px; height: 34px; border-radius: 9px; background: #1E88E5; color: #fff; font-weight: 800; font-size: 18px; display: flex; align-items: center; justify-content: center; }
  h1 { font-size: 19px; margin: 0; }
  .v { color: #6B7280; font-size: 11px; margin-top: 2px; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #1E88E5; margin: 16px 0 8px; }
  ol.steps { list-style: none; padding: 0; margin: 0; }
  ol.steps li { display: flex; gap: 9px; font-size: 12.5px; line-height: 1.45; margin-bottom: 7px; }
  ol.steps .n { flex: 0 0 20px; height: 20px; border-radius: 50%; background: #1E88E5; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  ul.tips { margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.5; color: #374151; }
  ul.tips li { margin-bottom: 4px; }
  .foot { margin-top: 14px; padding-top: 8px; border-top: 1px solid #E5E7EB; font-size: 10.5px; color: #6B7280; }
</style></head><body>
  <div class="head"><div class="logo">H</div><div><h1>${guide.title}</h1><div class="v">HinesTime · v9.0</div></div></div>
  <h2>${labels.steps}</h2><ol class="steps">${steps}</ol>
  <h2>${labels.tips}</h2><ul class="tips">${tips}</ul>
  <div class="foot">${labels.footer}</div>
</body></html>`;
}
